import React, { useRef, useImperativeHandle, useLayoutEffect, useState, useCallback } from "react";
import { v4 as uuid } from "uuid";
import { CheckboxField, Stack, Box, Alert } from "@curves/ui-base";
import { SignBox } from "@icoach/components";
import { NoticeCard, PrivacyNoticeCard } from "@icoach/documents/components";
import { getCheckboxValue, setCheckboxValue } from "@icoach/documents/tools";
import { getErrorModuleStatus, refIsRequiredError } from "@util/utils";
import { resolveTransferInNoticeList } from "@icoach/documents/membership/transferIn/transferInFormRules";
import privacyNotice from "@icoach/documents/membership/privacy/privacyNotice";
import { resolvePrivacyItemIndex } from "@icoach/documents/membership/create/versions";
import { useMembershipContext } from "@icoach/documents/membership/MembershipContext";

const MembershipTransferInSurvey = React.forwardRef((props, ref) => {
    const { temporarySourceDataRef, errorModule, isReadOnly } = useMembershipContext();
    const [refreshKey, setRefreshKey] = useState(null);
    const { originalContractVersion, originalIsRenew, memberType, contractSurvey } = temporarySourceDataRef.current;
    const { items = [], signImg, isConfirm } = contractSurvey || {};

    const itemsRef = useRef([]);
    const signImgRef = useRef(null);
    const isConfirmRef = useRef(null);

    // 須知清單改由 transferInFormRules 提供，Content 才能用同一份算出個資的索引。
    // 回傳 null 代表教練還沒填原會籍那一步，此時只顯示提示訊息。
    const noticeList = resolveTransferInNoticeList({ originalContractVersion, originalIsRenew, memberType });
    // 個資告知只有原會籍 106 的會員才需要重新簽署；101-105 回 null，不渲染卡片也不納入驗證。
    const privacyItemIndex = resolvePrivacyItemIndex(originalContractVersion);

    const renderNotice = useCallback(
        () => {
            if (!noticeList) {
                return (
                    <Alert className="mb-3" severity="info" variant="filled" color="info">
                        請先填寫原會籍與會籍調整步驟資訊，系統才會顯示對應的會員須知內容。
                    </Alert>
                );
            }

            return noticeList.map((item, index) => (
                <NoticeCard
                    key={index}
                    ref={el => (itemsRef.current[index] = el)}
                    defaultValue={items[index]}
                    title={item?.title}
                    img={item?.icon}
                    noticeData={item?.details}
                    WarnComponent={item?.WarnComponent}
                    readOnly={isReadOnly}
                />
            ));
        },
        // eslint-disable-next-line
        [originalContractVersion, originalIsRenew, memberType],
    );

    const setParams = () => {
        if (isReadOnly) return false;
        if (!contractSurvey) temporarySourceDataRef.current.contractSurvey = {};
        temporarySourceDataRef.current.contractSurvey.items = itemsRef?.current?.map(item => item?.getResult?.()?.checked);
        temporarySourceDataRef.current.contractSurvey.signImg = signImgRef.current?.getResult?.();
        temporarySourceDataRef.current.contractSurvey.isConfirm = getCheckboxValue(isConfirmRef.current?.getResult?.(), {
            type: "checked",
            isMultiple: false,
        });
    };

    useImperativeHandle(
        ref,
        () => ({
            getResult: () => setParams(),
            // 卡控條件與入會一致：個資卡片、總同意、簽名三項（七張運動須知維持非必填）。
            // 換頁被擋下時由 hook 呼叫，讓缺漏的欄位顯示紅字。
            isError: () => {
                const targets = [
                    privacyItemIndex === null ? null : itemsRef.current[privacyItemIndex],
                    isConfirmRef.current,
                    signImgRef.current,
                ];
                return refIsRequiredError.apply(null, targets);
            },
        }),
        // eslint-disable-next-line
        [privacyItemIndex],
    );

    useLayoutEffect(
        () => {
            setRefreshKey(uuid());
            return () => setParams();
        },
        // eslint-disable-next-line
        [],
    );

    return (
        <React.Fragment key={refreshKey}>
            <Box className="survey">
                {privacyItemIndex !== null && (
                    <PrivacyNoticeCard
                        ref={el => (itemsRef.current[privacyItemIndex] = el)}
                        notice={privacyNotice}
                        img={privacyNotice.icon}
                        defaultValue={items[privacyItemIndex]}
                        readOnly={isReadOnly}
                        required
                    />
                )}
                {renderNotice()}
                <Stack direction={"column"} justifyContent={"center"} alignItems={"center"}>
                    <CheckboxField
                        key={setCheckboxValue(isConfirm)}
                        ref={isConfirmRef}
                        defaultChecked={setCheckboxValue(isConfirm)}
                        {...getErrorModuleStatus(errorModule.contractSurvey, "isConfirm")}
                        readOnly={isReadOnly}
                        required
                    >
                        <CheckboxField.Checkbox label="本人已了解並詳讀以上會員須知，並同意遵守。" />
                    </CheckboxField>
                    <SignBox
                        ref={signImgRef}
                        title="會員簽名"
                        defaultValue={signImg}
                        {...getErrorModuleStatus(errorModule.contractSurvey, "signImg")}
                        readOnly={isReadOnly}
                    />
                </Stack>
            </Box>
        </React.Fragment>
    );
});

export default MembershipTransferInSurvey;
